<template>
  <div>
    <div class="d-flex justify-content-center mx-6">
      <SVehicleSearchResultDialog
        :isAnotherDateSelectionDialogOpen="isAnotherDateSelectionDialogOpen"
        :isRoundTrip="isRoundTrip"
        :isNeededVehicleFound="isNeededVehicleFound"
        :isOutwardVehicleFound="isOutwardVehicleFound"
        :isReturnVehicleFound="isReturnVehicleFound"
        :outwardState="getOutwardState"
        :returnState="getReturnState"
        @closeDialog="toMainPage"
        @trackGtmEventNoVehiclesFound="onTrackEventNoVehiclesFound"
      />
      <SServerErrorDialog
        :isServerErrorDialogOpen="isServerErrorDialogOpen"
        @closeDialog="toMainPage"
        @trackGtmEventServerErrorOccurred="onTrackEventServerErrorOccurred"
      />
      <SOnBoardPaymentErrorDialog
        :isOnBoardPaymentAvailableDialogOpen="isOnBoardPaymentAvailableDialogOpen"
        @closeDialog="isOnBoardPaymentAvailableDialogOpen = false"
        @trackGtmEventPayButtonClick="onTrackEventPayButtonClick"
      />
      <SPaymentDialog
        :isPaymentDialogOpen="isPaymentDialogOpen"
        :paymentType="getPaymentType"
        :paymentInfo="paymentInfo"
        @closePaymentDialog="isPaymentDialogOpen = false"
        @onPaymentSuccess="onPaymentSuccess"
        @trackGtmEventPayButtonClick="onTrackEventPayButtonClick"
      />
      <ValidationObserver
        ref="checkout"
        v-slot="{ invalid }"
        tag="div"
        name="checkout"
        class="breakpoint-xl"
      >
        <VRow
          dense
          :justify="!isExtraSmall ? 'space-between' : 'center'"
        >
          <VCol
            v-if="isExtraSmall"
            cols="12"
            class="pb-6"
          >
            <SMobileBookingSummary
              :isTripDetailsOpen="isTripDetailsOpen"
              :selectedRides="getSelectedRides"
              :getIsOpsZoneShouldBePrefilled="getIsOpsZoneShouldBePrefilled"
              @collapseTripDetails="isTripDetailsOpen = !isTripDetailsOpen"
            />
          </VCol>
          <VCol
            cols="12"
            lg="7"
            md="8"
            sm="6"
          >
            <PassengerInfo @onSignInSuccess="onSignInSuccess" />
          </VCol>
          <VCol
            cols="12"
            lg="3"
            md="4"
            sm="6"
          >
            <SCheckoutBookingSummary
              :isExtraSmall="isExtraSmall"
              :selectedRides="getSelectedRides"
              :getIsOpsZoneShouldBePrefilled="getIsOpsZoneShouldBePrefilled"
              :isAuth="!!getAuthToken"
              :invalid="invalid"
              termsAndConditionsPageName="terms-and-conditions"
              privacyPolicyPageName="https://www.slg.lu/en/politique-de-confidentialite/"
              :isCheckoutPage="isCheckoutPage"
              @updateAgreement="isAgree = !isAgree"
              @bookNow="bookNow"
            />
          </VCol>
        </VRow>
      </ValidationObserver>
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: 'Checkout',
};
</script>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import appConfig from '@/appConfig';
import {
  SPaymentTypeEnum, STripTypeEnum, SCheckoutErrorsEnum, SOnBoardPaymentErrorDialog, SOrderService,
  SMobileBookingSummary, SCheckoutBookingSummary, SVehicleSearchResultDialog, SPaymentDialog, SServerErrorDialog, SUserService,
} from '@slg/web-customer-shared';
import PassengerInfo from '@/components/checkout/passengerInfo/PassengerInfo.vue';
import { resolveReservationError } from './checkout.logic';
import {
  useStore, useRouter, useRouteRef, useVuetify, useVm, useGlobalErrorDialog, useGtm,
} from '@/composables';

const store = useStore();
const router = useRouter();
const route = useRouteRef();
const vuetify = useVuetify();
const vm = useVm();
const { setGlobalError } = useGlobalErrorDialog();
const { trackGtmEvent } = useGtm();

const isLoading = ref(true);
const isAgree = ref(false);
const isAnotherDateSelectionDialogOpen = ref(false);
const isTripDetailsOpen = ref(false);
const isNeededVehicleFound = ref<boolean | null>(null);
const isOutwardVehicleFound = ref<boolean | null>(null);
const isReturnVehicleFound = ref<boolean | null>(null);
const isPaymentDialogOpen = ref(false);
const isOnBoardPaymentAvailableDialogOpen = ref(false);
const isServerErrorDialogOpen = ref(false);
const paymentInfo = ref<any>(null);

const getTripType = computed(() => store.getters['booking/getTripType']);
const getAuthToken = computed(() => store.getters['auth/getAuthToken']);
const isFreeTrip = computed(() => store.getters['opsZone/isFreeTrip']);
const getSelectedRides = computed(() => store.getters['booking/getSelectedRides']);
const getPaymentType = computed(() => store.getters['booking/getPaymentType']);
const getPaymentMethod = computed(() => store.getters['booking/getPaymentMethod']);
const getIsFreeTrip = computed(() => store.getters['booking/getIsFreeTrip']);
const getBookingRequestId = computed(() => store.getters['booking/getBookingRequestId']);
const getPassengerPhone = computed(() => store.getters['booking/getPassengerPhone']);
const getPassengerFirstName = computed(() => store.getters['booking/getPassengerFirstName']);
const getPassengerLastName = computed(() => store.getters['booking/getPassengerLastName']);
const getPassengerEmail = computed(() => store.getters['booking/getPassengerEmail']);
const getUserDetailsForBookingDetails = computed(() => store.getters['auth/getUserDetailsForBookingDetails']);
const getCheckoutInfo = computed(() => store.getters['booking/getCheckoutInfo']);
const getOutwardState = computed(() => store.getters['booking/getOutwardState']);
const getReturnState = computed(() => store.getters['booking/getReturnState']);
const getReservationId = computed(() => store.getters['booking/getReservationId']);
const isAnotherPassenger = computed(() => store.getters['booking/isAnotherPassenger']);
const getAnotherPassengerList = computed(() => store.getters['auth/getAnotherPassengerList']);

const isRoundTrip = computed(() => getTripType.value === STripTypeEnum.ROUND_TRIP);
const isExtraSmall = computed(() => vuetify.breakpoint.xs);
const isCheckoutPage = computed(() => route.value.name === 'Checkout');
const getIsOpsZoneShouldBePrefilled = computed(() => appConfig.isOpsZoneShouldBePrefilled);

function onTrackEventNoVehiclesFound() {
  trackGtmEvent('The vehicle is not found dialog ok button click', 'Button', 'Click', 'Redirect to home page');
}

function onTrackEventServerErrorOccurred() {
  trackGtmEvent('Server error occurred dialog ok button', 'Button', 'Click', 'Redirect to home page');
}

function onTrackEventPayButtonClick() {
  trackGtmEvent('Pay button click', 'Button', 'Click', 'Confirm online payment');
}

async function onPaymentSuccess(data: any) {
  store.commit('booking/setIgnoreRemainingTime', true);
  isPaymentDialogOpen.value = false;
  const { orderId } = paymentInfo.value;

  const bookingCheckoutOrderModel = getPaymentType.value !== SPaymentTypeEnum.PRE_AUTHORIZATION
    ? {
      paymentMethodNonce: data.nonce,
      orderId,
    }
    : {
      paymentMethodId: getPaymentMethod.value,
      orderId,
    };

  store.commit('common/setGlobalLoading', true);
  try {
    await SOrderService.bookingCheckoutOrder(bookingCheckoutOrderModel);
    trackGtmEvent(
      'Booking completed successfully',
      'Special event',
      '',
      'Booking completed successfully, redirect to confirmation page',
    );
    store.commit('booking/clearTemporaryReservation');
    await router.push({ name: 'Confirmation', params: { orderId } });
  } catch (e: any) {
    if (e.status !== 401) {
      await setGlobalError(e, false);
    }
  }
  store.commit('common/setGlobalLoading', false);
  store.commit('booking/setIgnoreRemainingTime', false);
}

function setDefaultValuesToVehicleNotFoundDialog() {
  isNeededVehicleFound.value = false;
  isOutwardVehicleFound.value = false;
  isReturnVehicleFound.value = false;
  isAnotherDateSelectionDialogOpen.value = true;
}

function clearTemporaryReservationDeadline() {
  store.commit('booking/clearTemporaryReservationDeadline');
}

function createReservation(requestDelay: number, maxRequestDelay: number, spinnerText: string) {
  store.commit('common/setLabel', spinnerText);
  setTimeout(async () => {
    await store.dispatch('booking/setTemporaryReservation', getBookingRequestId.value)
      .then(() => {
        isNeededVehicleFound.value = true;
        isAnotherDateSelectionDialogOpen.value = true;
        setTimeout(() => {
          isAnotherDateSelectionDialogOpen.value = false;
        }, 1000);
      })
      .catch(async (error: any) => {
        if (error.status >= 500) {
          isServerErrorDialogOpen.value = true;
        } else if (error.status !== 401) {
          const {
            isNeededVehicleFound: needed,
            isOutwardVehicleFound: outward,
            isReturnVehicleFound: ret,
            isAnotherDateSelectionDialogOpen: another,
            isServerErrorDialogOpen: server,
            isWriteConflict,
          } = resolveReservationError(error, clearTemporaryReservationDeadline);
          isNeededVehicleFound.value = needed;
          isOutwardVehicleFound.value = outward;
          isReturnVehicleFound.value = ret;
          isAnotherDateSelectionDialogOpen.value = another;
          isServerErrorDialogOpen.value = server;
          requestDelay += 10000;

          if (isWriteConflict && requestDelay <= maxRequestDelay) {
            createReservation(
              requestDelay,
              maxRequestDelay,
              requestDelay === 10000 ? 'checkout.spinnerText10SecondsDelay' : 'checkout.spinnerText20or30SecondsDelay',
            );
          } else if (isWriteConflict && requestDelay > maxRequestDelay) {
            setDefaultValuesToVehicleNotFoundDialog();
          }
        }
      });

    if (isNeededVehicleFound.value || isAnotherDateSelectionDialogOpen.value) {
      store.commit('common/setGlobalLoading', false);
      store.commit('common/setLabel', null);
    }
  }, requestDelay);
}

function fillDefaultPassengerDetails() {
  store.commit('booking/setLoggedInPassengerDetails', getUserDetailsForBookingDetails.value);
}

function toMainPage() {
  isAnotherDateSelectionDialogOpen.value = false;
  router.push({ name: 'Booking' });
}

async function saveAnotherPassenger() {
  await SUserService.updateProfile({
    anotherPassengerList: [
      {
        passengerId: uuidv4(),
        phone: getPassengerPhone.value,
        firstName: getPassengerFirstName.value.trim(),
        lastName: getPassengerLastName.value.trim(),
      },
    ],
  });
}

async function bookNow() {
  if (getIsFreeTrip.value) {
    store.commit('booking/setPaymentType', SPaymentTypeEnum.NO_PAYMENT);
  }

  trackGtmEvent('Book now button click', 'Button', 'Click', 'Booking confirmation');

  (vm.$refs as any).checkout.validate().then(async (success: boolean) => {
    if (!success) {
      return;
    }
    nextTick(async () => {
      store.commit('common/setGlobalLoading', true);
      try {
        if (isAnotherPassenger.value && !getAnotherPassengerList.value.length) {
          await saveAnotherPassenger();
        }

        const bookingOrderModel = {
          bookingRequestId: getBookingRequestId.value,
          flightNumber: getCheckoutInfo.value.FlightDetails,
          locale: route.value.params.lang,
          outwardTripComment: getCheckoutInfo.value.commentForOutward,
          anotherPersonIsRiding: isAnotherPassenger.value,
          passengerDetails: [{
            phone: getPassengerPhone.value,
            firstName: getPassengerFirstName.value.trim(),
            lastName: getPassengerLastName.value.trim(),
            email: getPassengerEmail.value,
          }],
          paymentType: getPaymentType.value,
          returnTripComment: getCheckoutInfo.value.commentForReturn,
        };
        const now = new Date().valueOf();
        const { data } = await SOrderService.bookingOrderCreate(bookingOrderModel);
        paymentInfo.value = data;

        const refreshedRemainingTime = Math.round((data.reservedTill - now) / 1000);

        store.commit('booking/setRemainingTime', refreshedRemainingTime);

        if (!getPaymentMethod.value
          && (getPaymentType.value === SPaymentTypeEnum.PRE_PAID || getPaymentType.value === SPaymentTypeEnum.PRE_AUTHORIZATION)) {
          isPaymentDialogOpen.value = true;
        } else if (getPaymentMethod.value
          && (getPaymentType.value === SPaymentTypeEnum.PRE_PAID || getPaymentType.value === SPaymentTypeEnum.PRE_AUTHORIZATION)) {
          await onPaymentSuccess({ nonce: '' });
        } else {
          await onPaymentSuccess({ nonce: '' });
        }
      } catch (e: any) {
        if (e.status !== 401) {
          if (e.errorCode === SCheckoutErrorsEnum.D2D_BOOKING_PAYMENT_TYPE_NOT_ALLOWED) {
            isOnBoardPaymentAvailableDialogOpen.value = true;
          } else {
            await setGlobalError(e, true, { isErrorBookingConfirmation: true });
          }
        }
      }
      store.commit('common/setGlobalLoading', false);
    });
  });
}

function onSignInSuccess() {
  if (getAuthToken.value && !getReservationId.value) {
    fillDefaultPassengerDetails();
    store.commit('common/setGlobalLoading', true);
    const requestDelay = 0;
    const maxRequestDelay = 30000;
    createReservation(requestDelay, maxRequestDelay, 'checkout.spinnerText');
  }
}

onSignInSuccess();
</script>
<style lang="scss" scoped>

.booking-summary-container {
  box-shadow: 0 4px 24px 0 #00000014;
}

.image-wrapper {
  img {
    width: 281px;
    height: 181px;
  }
}
</style>
